import type {
  AccountingContinuity,
  AccountingExpUpdate,
  AccountingFrame,
  AccountingMesosUpdate,
  AccountingResourceUpdate,
  GameEvent,
  GameStateSnapshot,
  LiveRateMetric,
  LiveRateSnapshot,
  MetricKey,
  RankExpPoint,
  ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

type RateMetric = 'exp' | 'mesos' | 'hp' | 'mp'

const HOUR_MS = 3_600_000
const MAX_TRUSTED_INTERVAL_MS: Readonly<Record<RateMetric, number>> = {
  exp: 15_000,
  mesos: 15_000,
  hp: 2_000,
  mp: 2_000,
}
const EXACT_EXP_RATIO_TOLERANCE = 0.0002
const ESTIMATED_EXP_RATIO_TOLERANCE = 0.018
const RESOURCE_RATIO_TOLERANCE = 0.035
const EXP_WRAP_CONFIRMATION_MS = 15_000
const EXP_LIVE_STALE_MS = 15_000

interface MetricClock {
  lastSourceAt: number | null
  lastActiveAt: number | null
  pendingIntervalMs: number
  trustedDurationMs: number
  updatedAt: number | null
  hasBaseline: boolean
  available: boolean
}

interface ClockObservation {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
}

interface ExpPoint extends RankExpPoint {
  readonly ratio: number | null
  readonly estimated: boolean
  readonly sourceAt: number
}

interface PendingExpPoint {
  readonly point: ExpPoint
  readonly levelUpConfirmed: boolean
  readonly kind: 'change' | 'wrap'
}

interface PendingLevelUpTransition {
  readonly from: number
  readonly to: number
  readonly confirmedAt: number
  readonly anchorSourceAt: number
  readonly wrapSourceAt: number | null
}

interface PendingMesosValue {
  readonly value: number
}

interface ResourcePoint {
  readonly current: number
  readonly max: number | null
  readonly sourceAt: number
}

interface PendingResourcePoint {
  readonly point: ResourcePoint
}

export interface TrustedMetricLedgerAggregate {
  readonly expGained: number | null
  readonly expTrustedDurationMs: number
  readonly expPerHour: number | null
  readonly mesosStartBalance: number | null
  readonly mesosCurrentBalance: number | null
  readonly mesosBalanceChange: number | null
  readonly mesosDelta: number | null
  readonly mesosTrustedDurationMs: number
  readonly mesosPerHour: number | null
  readonly hpConsumed: number
  readonly hpConsumedPerHour: number | null
  readonly hpRecovered: number
  readonly hpRecoveredPerHour: number | null
  readonly mpConsumed: number
  readonly mpConsumedPerHour: number | null
  readonly mpRecovered: number
  readonly mpRecoveredPerHour: number | null
}

const emptyClock = (): MetricClock => ({
  lastSourceAt: null,
  lastActiveAt: null,
  pendingIntervalMs: 0,
  trustedDurationMs: 0,
  updatedAt: null,
  hasBaseline: false,
  available: false,
})

const emptyClocks = (): Record<RateMetric, MetricClock> => ({
  exp: emptyClock(),
  mesos: emptyClock(),
  hp: emptyClock(),
  mp: emptyClock(),
})

export class TrustedMetricLedger {
  private captureGeneration = 0
  private minimumSourceAt = 0
  private latestCapturedAt = 0
  private suspended = false
  private clocks = emptyClocks()

  private expExactAnchor: ExpPoint | null = null
  private expLiveAnchor: ExpPoint | null = null
  private expLatestConfirmed: ExpPoint | null = null
  private expPending: PendingExpPoint | null = null
  private expPendingLevelUp: PendingLevelUpTransition | null = null
  private expGained = 0
  private expExactPendingDurationMs = 0
  private expTrustedDurationMs = 0
  private hasExactExpBaseline = false

  private mesosBaseline: number | null = null
  private mesosPending: PendingMesosValue | null = null
  private mesosStartBalance: number | null = null
  private mesosCurrentBalance: number | null = null
  private mesosDelta = 0
  private hasMesosBaseline = false

  private resourceBaselines: Record<'hp' | 'mp', ResourcePoint | null> = { hp: null, mp: null }
  private resourcePending: Record<'hp' | 'mp', PendingResourcePoint | null> = { hp: null, mp: null }
  private resourceLastNumericAt: Record<'hp' | 'mp', number | null> = { hp: null, mp: null }
  private hasResourceBaseline: Record<'hp' | 'mp', boolean> = { hp: false, mp: false }
  private totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }

  constructor(private readonly preset: GamePreset) {}

  reset(at: number, captureGeneration = 0, initialState: GameStateSnapshot | null = null): void {
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.latestCapturedAt = at
    this.suspended = false
    this.clocks = emptyClocks()
    this.expExactAnchor = null
    this.expLiveAnchor = null
    this.expLatestConfirmed = null
    this.expPending = null
    this.expPendingLevelUp = null
    this.expGained = 0
    this.expExactPendingDurationMs = 0
    this.expTrustedDurationMs = 0
    this.hasExactExpBaseline = false
    this.mesosBaseline = null
    this.mesosPending = null
    this.mesosStartBalance = null
    this.mesosCurrentBalance = null
    this.mesosDelta = 0
    this.hasMesosBaseline = false
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
    this.hasResourceBaseline = { hp: false, mp: false }
    this.totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }
    this.seedInitialState(initialState)
  }

  suspend(): void {
    this.suspended = true
    this.rebaselineMetrics(false)
  }

  resume(at: number, captureGeneration: number): void {
    this.suspended = false
    this.changeGeneration(at, captureGeneration)
  }

  changeGeneration(at: number, captureGeneration: number): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.rebaselineMetrics(false)
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
    captureGeneration: number,
  ): AccountingFrame {
    this.changeGeneration(state.capturedAt, captureGeneration)
    this.latestCapturedAt = Math.max(this.latestCapturedAt, state.capturedAt)
    if (this.suspended) return this.emptyFrame(state.capturedAt)

    const exp = this.ingestExp(state, events, observation, activeAt)
    const mesos = this.ingestMesos(state, observation, activeAt)
    const hp = this.ingestResource('hp', state, observation, activeAt)
    const mp = this.ingestResource('mp', state, observation, activeAt)
    return { capturedAt: state.capturedAt, captureGeneration: this.captureGeneration, exp, mesos, hp, mp }
  }

  aggregate(): TrustedMetricLedgerAggregate {
    const mesosBalanceChange = this.mesosStartBalance === null || this.mesosCurrentBalance === null
      ? null
      : this.mesosCurrentBalance - this.mesosStartBalance
    return {
      expGained: this.hasExactExpBaseline ? this.expGained : null,
      expTrustedDurationMs: this.expTrustedDurationMs,
      expPerHour: this.rate(this.expGained, this.expTrustedDurationMs, this.hasExactExpBaseline),
      mesosStartBalance: this.mesosStartBalance,
      mesosCurrentBalance: this.mesosCurrentBalance,
      mesosBalanceChange,
      mesosDelta: this.hasMesosBaseline ? this.mesosDelta : null,
      mesosTrustedDurationMs: this.clocks.mesos.trustedDurationMs,
      mesosPerHour: this.rate(this.mesosDelta, this.clocks.mesos.trustedDurationMs, this.hasMesosBaseline),
      hpConsumed: this.totals.hpConsumed,
      hpConsumedPerHour: this.rate(this.totals.hpConsumed, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      hpRecovered: this.totals.hpRecovered,
      hpRecoveredPerHour: this.rate(this.totals.hpRecovered, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      mpConsumed: this.totals.mpConsumed,
      mpConsumedPerHour: this.rate(this.totals.mpConsumed, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
      mpRecovered: this.totals.mpRecovered,
      mpRecoveredPerHour: this.rate(this.totals.mpRecovered, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
    }
  }

  liveRates(now = this.latestCapturedAt): LiveRateSnapshot {
    return {
      expPerHour: this.liveExpMetric(now),
      mesosPerHour: this.liveMetric(this.mesosDelta, this.clocks.mesos),
      hpConsumedPerHour: this.liveMetric(this.totals.hpConsumed, this.clocks.hp),
      hpRecoveredPerHour: this.liveMetric(this.totals.hpRecovered, this.clocks.hp),
      mpConsumedPerHour: this.liveMetric(this.totals.mpConsumed, this.clocks.mp),
      mpRecoveredPerHour: this.liveMetric(this.totals.mpRecovered, this.clocks.mp),
    }
  }

  private ingestExp(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingExpUpdate {
    this.rememberLevelUpEvidence(events)
    if (observation?.accepted.exp && observation.expRebaseline) {
      const point = this.expPoint(state, observation)
      if (point && !point.estimated) return this.rebaselineExp(point, activeAt)
      return this.emptyExpUpdate('pending', observation.exp.capturedAt)
    }
    if (observation && !observation.accepted.exp) {
      const confirmedLevelUp = this.confirmLevelUpFromCarriedExp(state, events, activeAt)
      if (confirmedLevelUp) return confirmedLevelUp
      if (observation.exp.availability === 'unavailable') {
        if (this.pendingLevelUpIsActive(state.capturedAt)) {
          return this.emptyExpUpdate('pending', observation.exp.capturedAt)
        }
        this.markUnavailable('exp')
        this.resetExpSegment()
        return this.emptyExpUpdate('unavailable')
      }
      return this.emptyExpUpdate('pending', observation.exp.capturedAt)
    }
    const point = this.expPoint(state, observation)
    if (!point) return this.emptyExpUpdate('ignored')

    const sourceAt = observation?.exp.source === 'fused' && observation.exp.value?.ratio !== null
      ? state.capturedAt
      : point.sourceAt
    const clockObservation = this.observeClock('exp', sourceAt, activeAt)
    // OCR runs asynchronously. A bar sample from the same capture can reach the
    // ledger first, so a newer exact OCR endpoint may legitimately trail the
    // metric clock even though it has not been accounted for yet.
    const reconcilesDelayedExact = clockObservation.continuity === 'ignored'
      && this.canReconcileDelayedExact(point, state.capturedAt)
    const comparisonAnchor = point.estimated
      ? this.expLatestConfirmed
      : (this.expExactAnchor ?? this.expLatestConfirmed)
    const rememberedLevelUp = this.hasPendingLevelUp(this.expExactAnchor ?? comparisonAnchor, point, state.capturedAt)
    const confirmsDelayedWrap = clockObservation.continuity === 'ignored'
      && this.expPending?.kind === 'wrap'
      && (this.hasLevelUp(events, comparisonAnchor, point) || rememberedLevelUp)
    if (clockObservation.continuity === 'ignored' && !confirmsDelayedWrap && !reconcilesDelayedExact) {
      return this.emptyExpUpdate('ignored', sourceAt)
    }
    const continuity = confirmsDelayedWrap || reconcilesDelayedExact ? 'contiguous' : clockObservation.continuity
    if (continuity === 'baseline' || continuity === 'discontinuous') {
      this.establishExpBaseline(point)
      return this.emptyExpUpdate(continuity, sourceAt, point)
    }

    const levelUpConfirmed = this.hasLevelUp(events, comparisonAnchor, point) || rememberedLevelUp
    const strongEvidence = levelUpConfirmed || this.hasStrongExpEvidence(point, observation)
    const deltaFromLatest = comparisonAnchor
      ? this.calculateExpDelta(comparisonAnchor, point, levelUpConfirmed)
      : null

    if (deltaFromLatest === null) {
      if (comparisonAnchor && this.isPotentialExpWrap(comparisonAnchor, point)) {
        const pendingWrap = this.expPending?.kind === 'wrap' ? this.expPending : null
        if (!pendingWrap || sourceAt - pendingWrap.point.sourceAt <= EXP_WRAP_CONFIRMATION_MS) {
          if (!pendingWrap) this.expPending = { point, levelUpConfirmed: false, kind: 'wrap' }
          return this.emptyExpUpdate('pending', sourceAt, point)
        }
      }
      this.resetClockBaseline('exp', sourceAt, activeAt)
      this.establishExpBaseline(point)
      return this.emptyExpUpdate('discontinuous', sourceAt, point)
    }

    const unchanged = deltaFromLatest === 0
    if (!strongEvidence && !unchanged) {
      const pendingConfirmed = this.expPending !== null
        && this.expCandidateProgresses(this.expPending, point, levelUpConfirmed)
      if (!pendingConfirmed) {
        this.expPending = { point, levelUpConfirmed, kind: 'change' }
        return this.emptyExpUpdate('pending', sourceAt, point)
      }
    }

    return this.commitExpPoint(point, levelUpConfirmed, sourceAt, state.capturedAt)
  }

  private ingestMesos(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingMesosUpdate {
    if (observation && !observation.accepted.mesos) {
      if (observation.mesos.availability === 'unavailable') {
        this.markUnavailable('mesos')
        this.mesosBaseline = null
        this.mesosPending = null
        return this.emptyMesosUpdate('unavailable')
      }
      return this.emptyMesosUpdate('pending', observation.mesos.capturedAt)
    }
    if (state.mesos === null) return this.emptyMesosUpdate('ignored')
    const sourceAt = this.metricObservedAt(state, observation, 'mesos')
    if (sourceAt === null || sourceAt < this.minimumSourceAt) return this.emptyMesosUpdate('ignored', sourceAt)
    const clockObservation = this.observeClock('mesos', sourceAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyMesosUpdate('ignored', sourceAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous' || this.mesosBaseline === null) {
      this.mesosBaseline = state.mesos
      this.mesosPending = null
      this.mesosStartBalance ??= state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      return this.emptyMesosUpdate(clockObservation.continuity, sourceAt, state.mesos)
    }

    const delta = state.mesos - this.mesosBaseline
    const strongEvidence = observation === undefined || observation.mesos.confirmation === 'consensus'
    if (delta !== 0 && !strongEvidence) {
      const confirmsPending = this.mesosPending !== null
        && this.mesosCandidateProgresses(this.mesosBaseline, this.mesosPending.value, state.mesos)
      if (!confirmsPending) {
        this.mesosPending = { value: state.mesos }
        return this.emptyMesosUpdate('pending', sourceAt, state.mesos)
      }
    }

    const startValue = this.mesosBaseline
    this.mesosBaseline = state.mesos
    this.mesosPending = null
    this.mesosCurrentBalance = state.mesos
    this.mesosDelta += delta
    const trustedIntervalMs = this.commitClock('mesos')
    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs,
      delta,
      startValue,
      endValue: state.mesos,
    }
  }

  private ingestResource(
    metric: 'hp' | 'mp',
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingResourceUpdate {
    if (observation && !observation.accepted[metric]) {
      if (observation[metric].availability === 'unavailable') {
        this.markUnavailable(metric)
        this.resourceBaselines[metric] = null
        this.resourcePending[metric] = null
        this.resourceLastNumericAt[metric] = null
        return this.emptyResourceUpdate('unavailable')
      }
      return this.emptyResourceUpdate('pending', observation[metric].capturedAt)
    }
    const value = state[metric]
    if (value.current === null && value.ratio === null) return this.emptyResourceUpdate('ignored')

    const observedReading = observation?.[metric]
    const observedValue = observedReading?.value
    const hasVisibleBar = observedReading !== undefined
      && (observedReading.source === 'bar' || observedReading.source === 'fused')
      && observedValue?.ratio !== null
    const timelineAt = hasVisibleBar
      ? state.capturedAt
      : this.metricObservedAt(state, observation, metric)
    if (timelineAt === null) return this.emptyResourceUpdate('ignored')
    const clockObservation = this.observeClock(metric, timelineAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyResourceUpdate('ignored', timelineAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous') {
      this.resourceBaselines[metric] = null
      this.resourcePending[metric] = null
      this.resourceLastNumericAt[metric] = null
    }
    this.hasResourceBaseline[metric] = true

    const numericCurrent = observation ? observedValue?.current ?? null : value.current
    const numericMax = observation ? observedValue?.max ?? value.max : value.max
    const numericAt = numericCurrent === null ? null : this.metricObservedAt(state, observation, metric)
    if (numericCurrent === null || numericAt === null || numericAt < this.minimumSourceAt) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (this.resourceLastNumericAt[metric] !== null && numericAt <= this.resourceLastNumericAt[metric]!) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    this.resourceLastNumericAt[metric] = numericAt
    const point: ResourcePoint = { current: numericCurrent, max: numericMax, sourceAt: numericAt }
    const baseline = this.resourceBaselines[metric]
    if (!baseline) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (baseline.max !== null && point.max !== null && baseline.max !== point.max) {
      this.discardClockPending(metric)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('discontinuous', timelineAt)
    }

    const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)

    const delta = point.current - baseline.current
    if (delta === 0) {
      this.resourcePending[metric] = null
      this.resourceBaselines[metric] = point
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }

    const observedSource = observedReading?.source
    const sourcesAgree = observedSource !== undefined
      && this.resourceSourcesAgree(numericCurrent, numericMax, observedValue?.ratio ?? value.ratio, observedSource)
    if (observedSource === 'fused' && !sourcesAgree) {
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
    }
    const strongEvidence = observation === undefined || sourcesAgree
    if (!strongEvidence) {
      const pending = this.resourcePending[metric]
      if (!pending || !this.resourceCandidateProgresses(baseline.current, pending.point.current, point.current)) {
        this.resourcePending[metric] = { point }
        return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
      }
    }

    this.resourcePending[metric] = null
    this.resourceBaselines[metric] = point
    const consumed = delta < 0 ? Math.abs(delta) : 0
    const recovered = delta > 0 ? delta : 0
    if (metric === 'hp') {
      this.totals.hpConsumed += consumed
      this.totals.hpRecovered += recovered
    } else {
      this.totals.mpConsumed += consumed
      this.totals.mpRecovered += recovered
    }
    return { continuity: 'contiguous', sourceAt: timelineAt, trustedIntervalMs, consumed, recovered }
  }

  private observeClock(metric: RateMetric, sourceAt: number, activeAt: number): ClockObservation {
    const clock = this.clocks[metric]
    clock.available = true
    clock.updatedAt = sourceAt
    if (!clock.hasBaseline || clock.lastSourceAt === null || clock.lastActiveAt === null) {
      clock.hasBaseline = true
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'baseline', sourceAt }
    }
    if (sourceAt <= clock.lastSourceAt) return { continuity: 'ignored', sourceAt }
    const sourceGapMs = sourceAt - clock.lastSourceAt
    if (sourceGapMs > MAX_TRUSTED_INTERVAL_MS[metric]) {
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'discontinuous', sourceAt }
    }
    clock.pendingIntervalMs += Math.max(0, activeAt - clock.lastActiveAt)
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    return { continuity: 'contiguous', sourceAt }
  }

  private commitClock(metric: RateMetric): number {
    const clock = this.clocks[metric]
    const interval = Math.max(0, clock.pendingIntervalMs)
    clock.trustedDurationMs += interval
    clock.pendingIntervalMs = 0
    return interval
  }

  private commitContiguousClock(metric: RateMetric, continuity: AccountingContinuity): number {
    return continuity === 'contiguous' ? this.commitClock(metric) : 0
  }

  private discardClockPending(metric: RateMetric): void {
    this.clocks[metric].pendingIntervalMs = 0
  }

  private resetClockBaseline(metric: RateMetric, sourceAt: number, activeAt: number): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    clock.pendingIntervalMs = 0
    clock.hasBaseline = true
    clock.available = true
    clock.updatedAt = sourceAt
  }

  private markUnavailable(metric: RateMetric): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = null
    clock.lastActiveAt = null
    clock.pendingIntervalMs = 0
    clock.hasBaseline = false
    clock.available = false
  }

  private rebaselineMetrics(clearAvailability: boolean): void {
    for (const metric of Object.keys(this.clocks) as RateMetric[]) {
      const clock = this.clocks[metric]
      clock.lastSourceAt = null
      clock.lastActiveAt = null
      clock.pendingIntervalMs = 0
      clock.hasBaseline = false
      if (clearAvailability) clock.available = false
    }
    this.resetExpSegment()
    this.mesosBaseline = null
    this.mesosPending = null
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
  }

  private seedInitialState(state: GameStateSnapshot | null): void {
    if (!state) return
    const expSourceAt = state.observedAt?.exp ?? state.capturedAt
    if (state.level !== null && state.exp.absolute !== null && !state.exp.estimated) {
      const expPoint: ExpPoint = {
        level: state.level,
        absolute: state.exp.absolute,
        ratio: state.exp.ratio,
        estimated: false,
        sourceAt: expSourceAt,
      }
      if (this.validExpPoint(expPoint.level, expPoint.absolute, expPoint.ratio, true)) {
        this.establishExpBaseline(expPoint)
        this.resetClockBaseline('exp', expSourceAt, 0)
      }
    }

    const mesosSourceAt = state.observedAt?.mesos ?? state.capturedAt
    if (state.mesos !== null && Number.isSafeInteger(state.mesos) && state.mesos >= 0) {
      this.mesosBaseline = state.mesos
      this.mesosStartBalance = state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      this.resetClockBaseline('mesos', mesosSourceAt, 0)
    }
  }

  private resetExpSegment(): void {
    this.expExactAnchor = null
    this.expLiveAnchor = null
    this.expLatestConfirmed = null
    this.expPending = null
    this.expPendingLevelUp = null
    this.expExactPendingDurationMs = 0
  }

  private establishExpBaseline(point: ExpPoint): void {
    this.expLiveAnchor = point
    this.expLatestConfirmed = point
    this.expPending = null
    this.expPendingLevelUp = null
    this.expExactPendingDurationMs = 0
    this.expExactAnchor = point.estimated ? null : point
    if (!point.estimated) {
      this.hasExactExpBaseline = true
    }
  }

  private rebaselineExp(point: ExpPoint, activeAt: number): AccountingExpUpdate {
    this.resetClockBaseline('exp', point.sourceAt, activeAt)
    this.establishExpBaseline(point)
    return this.emptyExpUpdate('rebaseline', point.sourceAt, point)
  }

  private expPoint(state: GameStateSnapshot, observation: ValidatedObservation | undefined): ExpPoint | null {
    if (state.exp.absolute === null) return null
    const observedAt = this.metricObservedAt(state, observation, 'exp')
    if (observedAt === null || observedAt < this.minimumSourceAt) return null
    const rawHasAbsolute = observation?.exp.value?.absolute !== null && observation?.exp.value?.absolute !== undefined
    const exact = !state.exp.estimated && (observation === undefined || (rawHasAbsolute && observedAt >= this.minimumSourceAt))
    const level = this.resolveExpPointLevel(state.level, state.exp.absolute, state.exp.ratio, exact)
    if (level === null) return null
    return {
      level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      estimated: !exact,
      sourceAt: observedAt,
    }
  }

  private resolveExpPointLevel(reportedLevel: number | null, absolute: number, ratio: number | null, exact: boolean): number | null {
    if (reportedLevel !== null && this.validExpPoint(reportedLevel, absolute, ratio, exact)) return reportedLevel
    if (!exact || !this.preset.experienceTableAuthoritative || ratio === null || ratio <= 0) return null

    const candidates: number[] = []
    for (let level = 1; level <= 200; level += 1) {
      if (this.validExpPoint(level, absolute, ratio, true)) candidates.push(level)
      if (candidates.length > 1) return null
    }
    return candidates[0] ?? null
  }

  private validExpPoint(level: number, absolute: number, ratio: number | null, exact: boolean): boolean {
    if (!Number.isInteger(level) || level < 1 || !Number.isFinite(absolute) || absolute < 0) return false
    if (ratio !== null && (!Number.isFinite(ratio) || ratio < 0 || ratio > 1)) return false
    const required = this.preset.experienceToNext(level)
    if (required === null || required <= 0 || absolute > required) return false
    const tolerance = exact ? EXACT_EXP_RATIO_TOLERANCE : ESTIMATED_EXP_RATIO_TOLERANCE
    return ratio === null || Math.abs(absolute / required - ratio) <= tolerance
  }

  private confirmLevelUpFromCarriedExp(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    activeAt: number,
  ): AccountingExpUpdate | null {
    const anchor = this.expExactAnchor ?? this.expLatestConfirmed
    if (!anchor || state.level !== anchor.level + 1) return null
    const event = events.find((candidate) => candidate.type === 'level-up'
      && candidate.from === anchor.level
      && candidate.to === state.level)
    const point = this.expPoint(state, undefined)
    if (!point
      || point.level !== state.level
      || point.sourceAt <= anchor.sourceAt) {
      return null
    }
    const rememberedLevelUp = this.hasPendingLevelUp(anchor, point, state.capturedAt)
    if (!event && !rememberedLevelUp) return null
    const confirmedAt = event?.capturedAt ?? this.expPendingLevelUp?.confirmedAt ?? state.capturedAt
    if (Math.abs(confirmedAt - point.sourceAt) > EXP_WRAP_CONFIRMATION_MS) return null

    const clockObservation = this.observeClock('exp', point.sourceAt, activeAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous') {
      this.establishExpBaseline(point)
      return this.emptyExpUpdate(clockObservation.continuity, point.sourceAt, point)
    }
    return this.commitExpPoint(point, true, point.sourceAt, confirmedAt)
  }

  private commitExpPoint(
    point: ExpPoint,
    levelUpConfirmed: boolean,
    sourceAt: number,
    confirmedAt: number,
  ): AccountingExpUpdate {
    this.expPending = null
    const trustedIntervalMs = this.commitClock('exp')
    this.expExactPendingDurationMs += trustedIntervalMs
    const previousExact = this.expExactAnchor
    let exactDelta: number | null = null
    let exactIntervalMs = 0
    let exactStart: RankExpPoint | null = null
    let exactEnd: RankExpPoint | null = null

    if (!point.estimated) {
      if (previousExact) {
        const resolvedDelta = this.calculateExpDelta(previousExact, point, levelUpConfirmed)
        if (resolvedDelta !== null) {
          exactDelta = resolvedDelta
          exactIntervalMs = this.expExactPendingDurationMs
          exactStart = this.rankExpPoint(previousExact)
          exactEnd = this.rankExpPoint(point)
          this.expGained += resolvedDelta
          this.expTrustedDurationMs += exactIntervalMs
        }
      }
      this.expPendingLevelUp = null
      this.expExactAnchor = point
      this.hasExactExpBaseline = true
      this.expExactPendingDurationMs = 0
      this.expLiveAnchor = point
    } else {
      const anchor = this.expExactAnchor ?? this.expLiveAnchor
      if (levelUpConfirmed) this.rememberLevelUp(anchor, point, confirmedAt)
      this.expLiveAnchor = point
    }
    this.expLatestConfirmed = point

    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs: exactIntervalMs,
      delta: exactDelta,
      exact: !point.estimated,
      start: exactStart,
      end: exactEnd ?? (!point.estimated ? this.rankExpPoint(point) : null),
    }
  }

  private calculateExpDelta(previous: ExpPoint, current: ExpPoint, levelUpConfirmed: boolean): number | null {
    if (previous.level === current.level) {
      const required = this.preset.experienceToNext(current.level)
      const delta = (previous.estimated || current.estimated) && required !== null && previous.ratio !== null && current.ratio !== null
        ? Math.round((current.ratio - previous.ratio) * required)
        : current.absolute - previous.absolute
      return delta >= 0 ? delta : null
    }
    if (!levelUpConfirmed || current.level !== previous.level + 1) return null
    const required = this.preset.experienceToNext(previous.level)
    if (required === null || previous.absolute > required) return null
    return required - previous.absolute + current.absolute
  }

  private rememberLevelUp(previous: ExpPoint | null, current: ExpPoint, confirmedAt: number): void {
    if (!previous || current.level !== previous.level + 1) return
    const existing = this.expPendingLevelUp
    if (existing) {
      if (existing.from === previous.level
        && existing.to === current.level
        && existing.anchorSourceAt === previous.sourceAt
        && existing.wrapSourceAt === null) {
        this.expPendingLevelUp = { ...existing, wrapSourceAt: current.sourceAt }
      }
      return
    }
    this.expPendingLevelUp = {
      from: previous.level,
      to: current.level,
      confirmedAt,
      anchorSourceAt: previous.sourceAt,
      wrapSourceAt: current.sourceAt,
    }
  }

  private rememberLevelUpEvidence(events: readonly GameEvent[]): void {
    const anchor = this.expExactAnchor
    if (!anchor) return
    const event = events.find((candidate) => candidate.type === 'level-up'
      && candidate.from === anchor.level
      && candidate.to === anchor.level + 1)
    if (!event) return
    const existing = this.expPendingLevelUp
    if (existing
      && existing.from === event.from
      && existing.to === event.to
      && existing.anchorSourceAt === anchor.sourceAt) {
      return
    }
    this.expPendingLevelUp = {
      from: anchor.level,
      to: anchor.level + 1,
      confirmedAt: event.capturedAt,
      anchorSourceAt: anchor.sourceAt,
      wrapSourceAt: this.expPending?.kind === 'wrap' ? this.expPending.point.sourceAt : null,
    }
  }

  private pendingLevelUpIsActive(capturedAt: number): boolean {
    const pending = this.expPendingLevelUp
    if (!pending) return false
    if (capturedAt - pending.confirmedAt <= EXP_WRAP_CONFIRMATION_MS) return true
    this.expPendingLevelUp = null
    return false
  }

  private hasPendingLevelUp(previous: ExpPoint | null, current: ExpPoint, capturedAt: number): boolean {
    const pending = this.expPendingLevelUp
    if (!pending) return false
    const endpointSourceAt = pending.wrapSourceAt ?? current.sourceAt
    if (!this.pendingLevelUpIsActive(capturedAt)
      || Math.abs(endpointSourceAt - pending.confirmedAt) > EXP_WRAP_CONFIRMATION_MS) {
      this.expPendingLevelUp = null
      return false
    }
    return previous?.level === pending.from
      && previous.sourceAt === pending.anchorSourceAt
      && current.level === pending.to
      && current.sourceAt > pending.anchorSourceAt
      && current.sourceAt >= endpointSourceAt
  }

  private hasStrongExpEvidence(point: ExpPoint, observation: ValidatedObservation | undefined): boolean {
    return observation === undefined
      || (!point.estimated && observation.exp.source === 'fused' && observation.exp.value?.absolute !== null)
  }

  private canReconcileDelayedExact(point: ExpPoint, capturedAt: number): boolean {
    if (point.estimated || point.sourceAt < this.minimumSourceAt) return false
    if (capturedAt < point.sourceAt || capturedAt - point.sourceAt > MAX_TRUSTED_INTERVAL_MS.exp) return false
    const pendingExactSourceAt = this.expPending && !this.expPending.point.estimated
      ? this.expPending.point.sourceAt
      : null
    const latestExactSourceAt = Math.max(
      this.expExactAnchor?.sourceAt ?? -Infinity,
      pendingExactSourceAt ?? -Infinity,
    )
    return point.sourceAt > latestExactSourceAt
  }

  private expCandidateProgresses(pending: PendingExpPoint, current: ExpPoint, levelUpConfirmed: boolean): boolean {
    if (pending.kind === 'wrap') return false
    return this.calculateExpDelta(pending.point, current, pending.levelUpConfirmed || levelUpConfirmed) !== null
  }

  private isPotentialExpWrap(previous: ExpPoint, current: ExpPoint): boolean {
    return previous.level === current.level && current.absolute < previous.absolute
  }

  private hasLevelUp(events: readonly GameEvent[], previous: Pick<ExpPoint, 'level'> | null, current: Pick<ExpPoint, 'level'>): boolean {
    if (!previous || current.level !== previous.level + 1) return false
    return events.some((event) => event.type === 'level-up' && event.from === previous.level && event.to === current.level)
  }

  private mesosCandidateProgresses(baseline: number, pending: number, current: number): boolean {
    return this.candidateProgresses(baseline, pending, current)
  }

  private resourceSourcesAgree(current: number, max: number | null, ratio: number | null, source: string): boolean {
    if (source !== 'fused' || max === null || max <= 0 || ratio === null) return false
    return Math.abs(current / max - ratio) <= RESOURCE_RATIO_TOLERANCE
  }

  private resourceCandidateProgresses(baseline: number, pending: number, current: number): boolean {
    return this.candidateProgresses(baseline, pending, current)
  }

  private candidateProgresses(baseline: number, pending: number, current: number): boolean {
    const pendingDirection = Math.sign(pending - baseline)
    const currentDirection = Math.sign(current - baseline)
    return pendingDirection !== 0
      && currentDirection === pendingDirection
      && Math.abs(current - baseline) >= Math.abs(pending - baseline)
  }

  private metricObservedAt(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    metric: MetricKey,
  ): number | null {
    if (observation) return observation[metric].capturedAt
    return state.observedAt?.[metric] ?? state.capturedAt
  }

  private rankExpPoint(point: Pick<ExpPoint, 'level' | 'absolute'>): RankExpPoint {
    return { level: point.level, absolute: point.absolute }
  }

  private liveMetric(total: number, clock: MetricClock, durationMs = clock.trustedDurationMs): LiveRateMetric {
    if (!clock.available) {
      return { value: null, status: 'unavailable', trustedDurationMs: durationMs, updatedAt: clock.updatedAt }
    }
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return {
      value: Number.isFinite(value) ? value : 0,
      status: 'ready',
      trustedDurationMs: durationMs,
      updatedAt: clock.updatedAt,
    }
  }

  private liveExpMetric(now: number): LiveRateMetric {
    const pendingDelta = this.liveExpPendingDelta()
    const barConfirmsExact = pendingDelta === 0 && this.expExactAnchor !== null
    const updatedAt = barConfirmsExact
      ? this.expLiveAnchor?.sourceAt ?? this.expExactAnchor?.sourceAt ?? null
      : this.expExactAnchor?.sourceAt ?? null
    const freshnessAt = this.suspended ? this.latestCapturedAt : Math.max(now, this.latestCapturedAt)
    const available = this.clocks.exp.available
      && this.hasExactExpBaseline
      && updatedAt !== null
      && Math.max(0, freshnessAt - updatedAt) <= EXP_LIVE_STALE_MS
    if (!available) {
      return {
        value: null,
        status: 'unavailable',
        trustedDurationMs: this.expTrustedDurationMs,
        updatedAt,
      }
    }
    // A fresh bar that still agrees with the last exact endpoint can safely
    // extend live idle time. Its estimated gain never enters final accounting.
    const pendingDurationMs = barConfirmsExact
      ? this.expExactPendingDurationMs
      : 0
    const trustedDurationMs = this.expTrustedDurationMs + pendingDurationMs
    const value = trustedDurationMs > 0
      ? this.expGained / trustedDurationMs * HOUR_MS
      : 0
    return {
      value: Number.isFinite(value) ? value : 0,
      status: 'ready',
      trustedDurationMs,
      updatedAt,
    }
  }

  private liveExpPendingDelta(): number | null {
    const exact = this.expExactAnchor
    const live = this.expLiveAnchor
    if (!exact || !live) return null
    const levelUpConfirmed = live.level === exact.level + 1
      && this.expPendingLevelUp?.from === exact.level
      && this.expPendingLevelUp.to === live.level
      && this.expPendingLevelUp.anchorSourceAt === exact.sourceAt
    return this.calculateExpDelta(exact, live, levelUpConfirmed)
  }

  private rate(total: number, durationMs: number, hasBaseline: boolean): number | null {
    if (!hasBaseline) return null
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : 0
  }

  private emptyFrame(capturedAt: number): AccountingFrame {
    return {
      capturedAt,
      captureGeneration: this.captureGeneration,
      exp: this.emptyExpUpdate('ignored'),
      mesos: this.emptyMesosUpdate('ignored'),
      hp: this.emptyResourceUpdate('ignored'),
      mp: this.emptyResourceUpdate('ignored'),
    }
  }

  private emptyExpUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    point: ExpPoint | null = null,
  ): AccountingExpUpdate {
    return {
      continuity,
      sourceAt,
      trustedIntervalMs: 0,
      delta: null,
      exact: point ? !point.estimated : false,
      start: null,
      end: point && !point.estimated ? this.rankExpPoint(point) : null,
    }
  }

  private emptyMesosUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    value: number | null = null,
  ): AccountingMesosUpdate {
    return { continuity, sourceAt, trustedIntervalMs: 0, delta: null, startValue: null, endValue: value }
  }

  private emptyResourceUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    trustedIntervalMs = 0,
  ): AccountingResourceUpdate {
    return { continuity, sourceAt, trustedIntervalMs, consumed: 0, recovered: 0 }
  }
}
